import React, { ReactElement, useEffect, useRef, useState } from "react";
import portraits, { defaultPortrait } from "../assets";
import { GameState, LobbyState, Role } from "../types";
import { describeRecord } from "./history";
import { VoteHistoryTable, hasVoteHistory } from "./VoteHistory";
import Celebration from "./Celebration";
import Awards from "./Awards";
import AwardsCeremony from "./AwardsCeremony";
import { Award, computeAwards, pickAwards } from "./awards";
import "./GameOver.css";

type GameOverScreenProps = {
  gameState: GameState;
  user: string;
  onBackToLobby: () => void;
  onLeave: () => void;
};

/** Firework colours of each team. */
const FIREWORK_COLORS = {
  liberal: ["#7fbaf0", "#4f9be8", "#ffffff", "#f0be50"],
  fascist: ["#ff6a4d", "#ff9a85", "#f0be50", "#ffffff"],
};

const ROLE_LABEL: Record<string, string> = {
  [Role.LIBERAL]: "Liberal",
  [Role.FASCIST]: "Fascist",
  [Role.HITLER]: "Hitler",
};

/** Why the game ended, in one sentence. */
function reason(game: GameState): string {
  const hitler = game.playerOrder.find((p) => game.players[p].id === Role.HITLER);
  switch (game.state) {
    case LobbyState.FASCIST_VICTORY_ELECTION:
      return (hitler ?? game.chancellor) + " was Hitler, and the table elected them Chancellor.";
    case LobbyState.FASCIST_VICTORY_POLICY:
      return "The Fascists enacted their sixth policy.";
    case LobbyState.LIBERAL_VICTORY_POLICY:
      return "The Liberals enacted their fifth policy.";
    case LobbyState.LIBERAL_VICTORY_EXECUTION:
      return game.president + " executed " + (game.targetUser ?? hitler) + ", who was Hitler.";
    default:
      return "";
  }
}

/**
 * End C: full-screen results. Every role flips over one by one, followed by a round-by-round record of
 * the governments (sent by the server, so it is complete even after a reconnect).
 */
export default function GameOverScreen(props: GameOverScreenProps): ReactElement {
  // Picked once, so a state update while the screen is open doesn't reshuffle the awards.
  const [awards] = useState<Award[]>(() => pickAwards(computeAwards(props.gameState)));
  // The awards are announced one by one first; the results (and the fireworks) follow.
  const [ceremonyDone, setCeremonyDone] = useState(awards.length === 0);
  if (!ceremonyDone) {
    return <AwardsCeremony awards={awards} user={props.user} gameState={props.gameState} onDone={() => setCeremonyDone(true)} />;
  }
  return <GameOverResults {...props} awards={awards} />;
}

function GameOverResults(props: GameOverScreenProps & { awards: Award[] }): ReactElement {
  const game = props.gameState;
  const fascistWin =
    game.state === LobbyState.FASCIST_VICTORY_ELECTION || game.state === LobbyState.FASCIST_VICTORY_POLICY;
  const myRole = game.players[props.user]?.id;
  const myTeamFascist = myRole === Role.FASCIST || myRole === Role.HITLER;
  const won = myRole !== undefined && myTeamFascist === fascistWin;
  const history = game.history ?? [];

  // Show the end of the timeline when it is longer than the screen.
  const timeline = useRef<HTMLDivElement>(null);
  const backButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (timeline.current) {
      timeline.current.scrollLeft = timeline.current.scrollWidth;
    }
    // preventScroll: the title and the fireworks must stay in view when the screen opens.
    backButton.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      className={"go-screen " + (fascistWin ? "go-fascist" : "go-liberal")}
      role="dialog"
      aria-modal="true"
      aria-labelledby="go-title"
    >
      {/* Fireworks are for the winning team only; the losing team just sees the result. */}
      {won && <Celebration colors={fascistWin ? FIREWORK_COLORS.fascist : FIREWORK_COLORS.liberal} />}
      <div className="go-inner">
        <div className="go-kicker">
          GAME OVER{game.round ? " · ROUND " + game.round : ""}
        </div>
        <h1 id="go-title" className="go-title">
          {fascistWin ? "Fascist victory" : "Liberal victory"}
        </h1>
        <div className="go-reason">{reason(game)}</div>
        {myRole && (
          <div className={"go-you " + (won ? "go-you-won" : "go-you-lost")}>
            You were {myRole === Role.HITLER ? "Hitler" : "a " + ROLE_LABEL[myRole]} · you {won ? "won" : "lost"}
          </div>
        )}

        <div className="go-cards">
          {game.playerOrder.map((name, i) => {
            const role = game.players[name].id;
            const icon = game.icon[name];
            const portrait = icon && icon !== defaultPortrait ? portraits[icon] : undefined;
            const roleClass = role ? role.toLowerCase() : "unknown";
            return (
              <div
                key={name}
                className={
                  "go-card go-card-" + roleClass +
                  (name === props.user ? " go-card-you" : "") +
                  (!game.players[name].alive ? " go-card-dead" : "")
                }
                style={{ animationDelay: i * 0.15 + "s" }}
              >
                {portrait ? (
                  <img src={portrait} alt="" className="go-avatar" />
                ) : (
                  <div className="go-avatar go-avatar-empty">{name.charAt(0)}</div>
                )}
                <div className="go-name">
                  {name}
                  {name === props.user ? " (you)" : ""}
                </div>
                <div className="go-role">{role ? ROLE_LABEL[role] : "?"}</div>
                {!game.players[name].alive && <div className="go-dead">Executed</div>}
              </div>
            );
          })}
        </div>

        <Awards gameState={game} user={props.user} awards={props.awards} />

        {history.length > 0 && (
          <div className="go-history">
            <div className="go-history-title">HOW IT WENT</div>
            <div className="go-timeline" ref={timeline}>
              {history.map((record, i) => {
                const d = describeRecord(record);
                return (
                  <div key={i} className={"go-round go-round-" + d.kind}>
                    <div className="go-round-n">Round {record.round}</div>
                    <div className="go-round-gov">{d.government}</div>
                    <div className={"go-chip go-chip-" + d.kind}>{d.result}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {hasVoteHistory(game) && (
          <div className="go-history go-votes">
            <div className="go-history-title">HOW EVERYONE VOTED</div>
            <VoteHistoryTable gameState={game} user={props.user} />
          </div>
        )}

        <div className="go-actions">
          <button ref={backButton} className="gold-button gold-button-large" onClick={props.onBackToLobby}>
            <span className="gold-label">BACK TO LOBBY</span>
          </button>
          <button className="go-leave" onClick={props.onLeave}>
            Leave game
          </button>
        </div>
      </div>
    </div>
  );
}
